<template>
  <div class="field-group">
    <label :for="id">{{ label }}</label>

    <button
      :id="id"
      class="select-field"
      :class="{ 'is-error': !!errorMessage }"
      type="button"
      :disabled="disabled"
      @click="$emit('toggle')"
      @blur="$emit('blur')"
    >
      <span>{{ displayValue }}</span>
      <span class="chevron">⌄</span>
    </button>

    <div v-if="open" class="options-panel">
      <button
        v-for="option in options"
        :key="getOptionValue(option)"
        type="button"
        class="option-item"
        :class="{ active: modelValue === getOptionValue(option) }"
        @click="$emit('select', getOptionValue(option))"
      >
        {{ getOptionLabel(option) }}
      </button>
    </div>

    <small v-if="errorMessage" class="error-text">{{ errorMessage }}</small>
    <small v-else-if="warningMessage" class="warning-text">{{
      warningMessage
    }}</small>
    <small v-else-if="hint">{{ hint }}</small>
  </div>
</template>

<script>
export default {
  name: 'BaseSelect',
  props: {
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Select an option',
    },
    options: {
      type: Array,
      default: () => [],
    },
    open: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    warningMessage: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle', 'select', 'blur'],
  computed: {
    displayValue() {
      const selected = this.options.find(
        (option) => this.getOptionValue(option) === this.modelValue,
      )
      return selected ? this.getOptionLabel(selected) : this.placeholder
    },
  },
  methods: {
    getOptionValue(option) {
      return typeof option === 'object'
        ? option.value ?? option.code ?? option.label
        : option
    },
    getOptionLabel(option) {
      return typeof option === 'object'
        ? option.label ?? option.code ?? option.value
        : option
    },
  },
}
</script>

<style scoped>
.field-group {
  display: flex;
  flex-direction: column;
}

.field-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #36442c;
}

.select-field {
  width: 100%;
  min-height: 58px;
  padding: 0 16px;
  border-radius: 16px;
  border: 1.5px solid #b8c2aa;
  background: #ffffff;
  font-size: 16px;
  color: #26311d;
  outline: none;
  box-shadow: 0 6px 14px rgba(68, 85, 38, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
}

.select-field:focus {
  border-color: #fcc415;
  box-shadow: 0 0 0 4px rgba(252, 196, 21, 0.18),
    0 9px 18px rgba(68, 85, 38, 0.08);
}

.select-field.is-error {
  border-color: #cf7d7d;
  box-shadow: 0 6px 14px rgba(207, 125, 125, 0.08);
}

.select-field.is-error:focus {
  border-color: #cf7d7d;
  box-shadow: 0 0 0 4px rgba(207, 125, 125, 0.14),
    0 9px 18px rgba(207, 125, 125, 0.12);
}

.select-field:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.chevron {
  font-size: 18px;
  color: #556245;
}

.options-panel {
  margin-top: 8px;
  border: 1px solid #cfd7c1;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 12px 26px rgba(34, 40, 24, 0.1);
  overflow: hidden;
}

.option-item {
  width: 100%;
  border: 0;
  background: #ffffff;
  text-align: left;
  padding: 14px 16px;
  font-size: 15px;
  color: #26311d;
}

.option-item + .option-item {
  border-top: 1px solid #edf0e8;
}

.option-item.active {
  background: rgba(252, 196, 21, 0.14);
  color: #445526;
  font-weight: 700;
}

.field-group small {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #5d6657;
}

.error-text {
  display: block;
  margin-top: 8px;
  color: #b05b5b !important;
  font-size: 14px;
  line-height: 1.5;
}

.warning-text {
  display: block;
  margin-top: 8px;
  color: #b05b5b !important;
  font-size: 14px !important;
  line-height: 1.5;
  font-weight: 600 !important;
}
</style>
